







































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import smoothReflow from 'vue-smooth-reflow';
import OrderV2, { OrderV3 as Order, v2ToV3 } from '@/zenky/interfaces/orders/Order';
import ProductsSection, { ChangeQuantityEvent as ChangeVariantQuantityEvent }
  from '@/components/forms/fields/orders/ProductsSection.vue';
import CustomerSection from '@/components/forms/fields/orders/CustomerSection.vue';
import DeliverySection, { ChangeEvent as DeliverySectionChangeEvent }
  from '@/components/forms/fields/orders/DeliverySection.vue';
import PaymentSection, { CreatePaymentEvent, RefundEvent as RefundPaymentEvent,
  CancelPendingRefundEvent as CancelPendingRefundPaymentEvent }
  from '@/components/forms/fields/orders/PaymentSection.vue';
import OrderHeader, { CitySelectEvent, StatusSelectEvent } from '@/components/forms/fields/orders/Header.vue';
import axios from '@/zenky/api/Http';
import { AxiosError } from 'axios';
import ItemResponse from '@/zenky/api/responses/ItemResponse';
import ErrorResponse from '@/zenky/api/responses/ErrorResponse';
import ProductVariant from '@/zenky/interfaces/products/ProductVariant';
import * as PostRequests from '@/zenky/interfaces/orders/PostRequests';
import { getZeroPriceStub, assertNever, parseApiDatetimeUtc } from '@/helpers/utils';
import { CreatePaymentTransaction, RefundPaymentTransaction } from '@/zenky/interfaces/orders/payments/PostRequests';
import OrderStatusKind from '@/zenky/interfaces/orders/OrderStatusKind';
import City from '@/zenky/interfaces/store/City';
import { Omit } from 'lodash';
import DeliveryMethod from '@/zenky/interfaces/orders/DeliveryMethod';
import MultiNProgress from 'multi-nprogress';
import { randomId } from '@/helpers';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { debounce } from 'debounce';
import store from '@/store';

type RequiredOrderPart = {
  /* eslint-disable camelcase */
  variants: ProductsSection['orderVariants'];
  payments: PaymentSection['payments'];
  payments_summary: PaymentSection['paymentsSummary'];
  total_price: ProductsSection['total'];
  city: OrderHeader['city'] & DeliverySection['value']['city'];
  delivery_address: null | Omit<DeliverySection['value'], 'city' | 'stock' | 'method' | 'deliverAt'>;
  stock: DeliverySection['value']['stock'] & DeliverySection['value']['stock'];
  submitted_at: OrderHeader['submittedAt'];
  number: OrderHeader['number'];
  current_status: { status: OrderHeader['status'] };
  customer: CustomerSection['orderCustomer'];
  /* eslint-enable camelcase */
};
type ApiResponseOrder = Order & Required<Pick<Order, keyof RequiredOrderPart>>
  & { // Ugly, I know. Need to mark deeper layers of Order as required.
    variants: Array<Exclude<Order['variants'], undefined>[number] & {
      product: Exclude<Exclude<Order['variants'], undefined>[number]['product'], undefined>,
      variant: Exclude<Exclude<Order['variants'], undefined>[number]['variant'], undefined>,
    }>
  };
const orderIncludes = [
  'variants',
  'variants.product',
  'variants.variant',
  'payments',
  'payments_summary',
  'city',
  'stock',
  'delivery_address',
  'customer',
];
if (process.env.NODE_ENV !== 'production') {
  // Since there's no TypeScript type checking for templates, we're checking it this way whether an order object
  // returned by API can be passed to child components.
  const test: RequiredOrderPart = 'asd' as unknown as ApiResponseOrder;
}

type StubOrder = RequiredOrderPart & { stubOrder: true };
function isStubOrder(order: ApiResponseOrder | StubOrder): order is StubOrder {
  const key: keyof StubOrder = 'stubOrder';
  return key in order;
}

// As you can see, this type does not include all the order data. Some data does not need to be
// copied / buffered / computed, it can be used directly and its changes don't need to be temporarily stored – they are
// immediatly sent to the server.
type DraftOrderData = {
  newCustomer: CustomerSection['newCustomer'] | null;
  delivery: Omit<DeliverySection['value'], 'city'>;
  notes: string;
}

function getDeliveryAddressApiData(
  delivery: DraftOrderData['delivery'], cityName: string,
): PostRequests.SubmitOrderAddress {
  if (delivery.street === null) throw new Error('Street must be selected');

  return {
    address: `${cityName}, ${delivery.street}, ${delivery.house}`,
    apartment: delivery.apartment,
    entrance: delivery.entrance,
    has_intercom: delivery.has_intercom,
    floor: delivery.floor,
  };
}
function getDeliverAtApiData(
  deliverAt: DraftOrderData['delivery']['deliverAt'],
): PostRequests.SubmitOrder['deliver_at'] {
  if (deliverAt === undefined) throw new Error('Delivery type is not ASAP, but delivery time is not set');
  return deliverAt === null ? undefined : deliverAt;
}

type PostWithPinReturnType = { type: 'incorrect-pin' } | { type: 'success' } | { type: 'unhandled', err: AxiosError }
async function postWithPin(url: string, payload: object | undefined, pin: string): Promise<PostWithPinReturnType> {
  try {
    await axios.post(
      url,
      payload,
      { headers: { 'X-User-Pin': pin } },
    );
  } catch (errAny) {
    const err = errAny as AxiosError;
    if (err.response === undefined || (err.response.data as ErrorResponse).error.error_code !== 'auth.invalid_pin') {
      return { type: 'unhandled', err };
    }
    return { type: 'incorrect-pin' };
  }
  return { type: 'success' };
}


const newCustomerDataFactory: () => CustomerSection['newCustomer'] = () => ({
  first_name: '',
  last_name: '',
  phone: '',
  phone_country: 'RU', // TODO recognize for other countries.
  gender: undefined,
  birth_date: '',
});

const initialDraftOrderDataFactory: () => DraftOrderData = () => ({
  newCustomer: null,
  delivery: {
    stock: null,
    method: 'courier',
    street: null,
    house: '',
    apartment: '',
    entrance: '',
    floor: '',
    has_intercom: false,
    deliverAt: null,
  },
  notes: '',
});

const inputDebounceTime = 2000;

@Component({
  components: { ProductsSection, PaymentSection, DeliverySection, OrderHeader, CustomerSection },
  mixins: [smoothReflow],
})
export default class CreateEditOrderPage extends Vue {
  @Prop({
    required: true,
    validator: (val: any) => (typeof val === 'string' || val === null),
  }) readonly orderId!: string | null; // `null` means that it's a new order.
  private readonly formId = randomId('order-form');
  progressBar = new MultiNProgress();
  order: ApiResponseOrder | StubOrder | null = null;
  draftOrderData: DraftOrderData = initialDraftOrderDataFactory();

  private readonly isStubOrder = isStubOrder;

  mounted() {
    const { status } = this.progressBar;
    this.progressBar.remove(); // Otherwise it also stays within <body> after we've closed the sidebar for some reason.
    this.progressBar.configure({
      parent: `#${this.headerId}`,
      showSpinner: false,
    });
    if (status !== null) {
      this.progressBar.set(status);
    }
    const sectionWrappers = (this.$refs.main as Element).children;
    for (let wrapperI = 0; wrapperI < sectionWrappers.length; wrapperI += 1) {
      this.$smoothReflow!({ el: sectionWrappers[wrapperI], transition: 'height 0.3s', hideOverflow: false });
    }
  }
  beforeDestroy() {
    // TODO do not load the order after closing, just change it?
    // TODO warning on unsaved changes (utilize ValidationObserver?).
    this.debounceSetDeliveryInfo.flush();
    this.debounceSetNotes.flush();

    this.progressBar.remove();
  }

  @Watch('orderId', { immediate: true })
  async onOrderIdChange(newVal: this['orderId'], oldVal: this['orderId']) {
    if (newVal === null) {
      // Create an order stub so we can display stuff like "total price: 0" while we don't have an actual order object.
      const zeroPriceStub = getZeroPriceStub();
      this.order = {
        stubOrder: true,
        variants: [],
        total_price: zeroPriceStub,
        payments: [],
        payments_summary: {
          amount_to_pay: zeroPriceStub,
          amount_to_confirm: zeroPriceStub,
          amount_paid: {
            total: zeroPriceStub,
            confirmed: zeroPriceStub,
            unconfirmed: zeroPriceStub,
          },
        },
        city: null,
        number: '',
        submitted_at: null,
        current_status: {
          status: {
            stubStatus: true,
            id: 'stub',
            kind: { id: 'stub', name: 'Черновик' },
            name: 'Черновик',
            background_color: '#CDB9B5',
          },
        },
        stock: null,
        delivery_address: null,
        customer: null,
      };
      this.draftOrderData = initialDraftOrderDataFactory();
    } else {
      const keepOldOrderDataUntilNewLoaded = oldVal === null && typeof newVal === 'string';
      if (!keepOldOrderDataUntilNewLoaded) {
        this.order = null;
      }
      await this.loadOrder(true);
    }
  }
  // TODO get loading(): boolean {

  async loadOrder(overrideInputs: boolean = false) {
    if (this.orderId === null) { throw new Error('Attempted to load order in order creation form'); }
    this.progressBar.start(); // Calling `start` when it's already going will do nothing.
    const request = axios.get(`orders/${this.orderId}`, {
      params: { with: orderIncludes },
      onDownloadProgress: () => this.progressBar.inc(),
      onUploadProgress: () => this.progressBar.inc(),
    });
    const orderV2 = ((await request).data as ItemResponse<OrderV2>).data;
    this.progressBar.done();
    this.order = v2ToV3(orderV2) as ApiResponseOrder;
    if (overrideInputs) {
      let draftDeliveryData: this['draftOrderData']['delivery'];
      if (this.order.delivery_address === null) {
        draftDeliveryData = initialDraftOrderDataFactory().delivery;
      } else {
        let deliveryPlaceDeliveryTime: typeof draftDeliveryData['deliverAt'];
        if (this.order.deliver_at === null) {
          deliveryPlaceDeliveryTime = null;
        } else {
          const date = parseApiDatetimeUtc(this.order.deliver_at.datetime_utc);
          const str = date.toLocaleString('en-CA', {
            timeZone: this.order.city!.timezone,
            hour12: false,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          });
          // YYYY-MM-DD HH:MM
          // eslint-disable-next-line prefer-template
          deliveryPlaceDeliveryTime = str.slice(0, 4) + '-'
            + str.slice(5, 7) + '-'
            + str.slice(8, 10) + ' '
            + str.slice(12, 14) + ':'
            + str.slice(15, 17);
        }
        draftDeliveryData = {
          stock: this.order.stock,
          house: this.order.delivery_address.house,
          apartment: this.order.delivery_address.apartment,
          floor: this.order.delivery_address.floor,
          has_intercom: this.order.delivery_address.has_intercom,
          entrance: this.order.delivery_address.entrance,
          method: this.order.delivery_method.id === DeliveryMethod.DELIVERY ? 'courier' : this.order.delivery_method.id,
          street: this.order.delivery_address.street,
          deliverAt: deliveryPlaceDeliveryTime,
        };
      }
      this.draftOrderData = {
        newCustomer: null,
        delivery: draftDeliveryData,
        notes: this.order.notes,
      };
    }
  }
  async changeOrder(config: Parameters<typeof axios['request']>[0]) {
    this.progressBar.start();
    await axios.request({
      method: 'POST',
      onDownloadProgress: () => this.progressBar.inc(),
      onUploadProgress: () => this.progressBar.inc(),
      ...config,
    });
    await this.loadOrder(); // `progressBar.done` is called within this method.
  }
  async createOrder(createOrderData: PostRequests.CreateOrder) {
    // TODO try-catch.
    this.progressBar.start();
    const request = axios.post('orders', createOrderData, {
      onDownloadProgress: () => this.progressBar.inc(),
      onUploadProgress: () => this.progressBar.inc(),
    });
    const createdOrder = ((await request).data as ItemResponse<Order>).data;
    // `orderId` watcher will load the order.
    this.$router.push({ name: 'orders.edit', params: { id: createdOrder.id } });
  }
  async trySubmitOrder() {
    if (this.order === null) { return; }
    if (isStubOrder(this.order)) throw new Error('Attempted to submit an order, but it is not created');
    if (this.order.status.id !== OrderStatusKind.PENDING) throw new Error('Attempted to submit a submitted order');

    if ((await this.validate()) === false) {
      return;
    }

    const { delivery } = this.draftOrderData;
    // TODO validation
    let deliveryAddress: PostRequests.SubmitOrder['delivery_address'];
    const deliveryMethod:
      Exclude<PostRequests.SubmitOrder['delivery_method'], undefined> = delivery.method === 'courier'
        ? DeliveryMethod.DELIVERY
        : delivery.method;
    switch (deliveryMethod) {
      case 'pickup': {
        deliveryAddress = undefined;
        break;
      }
      case 'delivery': {
        if (delivery.street === null) throw new Error('Street must be selected');
        if (this.order.city === null) throw new Error('City must be selected');
        deliveryAddress = getDeliveryAddressApiData(delivery, this.order.city.name);
        break;
      }
      default: assertNever(deliveryMethod);
    }
    const payload: PostRequests.SubmitOrder = {
      delivery_method: deliveryMethod,
      delivery_address: deliveryAddress,
      notes: this.draftOrderData.notes,
    };
    if (delivery.deliverAt !== null) {
      // Not ASAP.
      payload.deliver_at = getDeliverAtApiData(delivery.deliverAt);
    }

    const storeRecaptchaSettings = store.state.auth.store!.settings!.recaptcha;
    const currentActionId: typeof storeRecaptchaSettings['actions'][number]['id'] = 'order_submission';
    if (
      storeRecaptchaSettings.enabled
      && storeRecaptchaSettings.actions.find(action => action.id === currentActionId) !== undefined
    ) {
      if (store.state.recaptcha.instance === null) throw new Error('Expected recaptcha to be loading at this point');
      const validInstance = (await store.state.recaptcha.instance);
      const token = await validInstance.execute(storeRecaptchaSettings.key, { action: currentActionId });
      payload.recaptcha = { token };
    }
    await this.changeOrder({
      url: `orders/${this.order.id}/submit`,
      data: payload,
    });
  }

  async setCity(event: CitySelectEvent) {
    if (this.order === null) {
      return; // TODO?
    }
    if (isStubOrder(this.order)) {
      await this.createOrder({ city_id: event });
    } else {
      await this.changeOrder({
        url: `orders/${this.order.id}/city`,
        data: { city_id: event },
      });
    }
  }
  async setStatus(event: StatusSelectEvent) {
    if (this.order === null) { return; } // TODO?
    if (isStubOrder(this.order)) {
      throw new Error('Can\'t change stub order status. It must be created first');
    }
    await this.changeOrder({
      url: `orders/${this.order.id}/status`,
      data: { order_status_id: event.id },
    });
  }

  async changeVariantQuantity($event: ChangeVariantQuantityEvent) {
    if (this.order === null) {
      return; // TODO queue?
    }
    let quantityDelta: number;
    if (isStubOrder(this.order)) {
      quantityDelta = $event.type === 'set' ? $event.newQuantity : $event.quantityDelta;
      if (quantityDelta < 0) {
        return;
      }
      return this.createOrder({
        variants: [{ product_variant_id: $event.variantId, quantity: quantityDelta }],
      });
    }
    if ($event.type === 'incOrDec') {
      quantityDelta = $event.quantityDelta; // eslint-disable-line prefer-destructuring
    } else {
      const matchedOrderVariant = this.order.variants.find(
        orderVariant => orderVariant.product_variant_id === $event.variantId,
      );
      quantityDelta = $event.newQuantity - (matchedOrderVariant === undefined ? 0 : matchedOrderVariant.quantity);
    }
    let request;
    if (quantityDelta > 0) {
      const payload: PostRequests.AddOrderProductVariant = {
        product_variant_id: $event.variantId,
        quantity: quantityDelta,
      };
      request = this.changeOrder({
        url: `orders/${this.order.id}/products`,
        data: payload,
      });
    } else if (quantityDelta < 0) {
      const payload: PostRequests.RemoveOrderProductVariant = {
        product_variant_id: $event.variantId,
        quantity: -quantityDelta,
      };
      request = this.changeOrder({
        url: `orders/${this.order.id}/products/remove`,
        data: payload,
      });
    }
    await request;
    const variantRecord = this.order.variants.find(v => v.product_variant_id === $event.variantId);
    const newQuantity = variantRecord !== undefined ? variantRecord.quantity : 0;
    $event.resolve(newQuantity);
  }

  customerEnterCreateMode() {
    this.draftOrderData.newCustomer = newCustomerDataFactory();
  }
  customerCancelCreate() {
    this.draftOrderData.newCustomer = null;
  }
  async tryAttachNewCustomer() {
    if (this.order === null) return;
    if (isStubOrder(this.order)) throw new Error('Attempted to create a customer, but order is not created yet.');
    const { newCustomer } = this.draftOrderData;
    if (newCustomer === null) {
      throw new Error('Attempted to create a customer while not in customer creation mode');
    }

    const customerValid = await ((this.$refs.customerSection as CustomerSection)
      .$refs.validationObserver as InstanceType<typeof ValidationObserver>).validate();
    if (!customerValid) {
      return;
    }

    const payload: PostRequests.SubmitOrderCustomer = {
      first_name: newCustomer.first_name === '' ? undefined : newCustomer.first_name,
      last_name: newCustomer.last_name === '' ? undefined : newCustomer.last_name,
      phone: newCustomer.phone === '' ? undefined : newCustomer.phone,
      phone_country: newCustomer.phone_country,
      gender: newCustomer.gender,
      birth_date: ((s: string) => `${s.slice(6, 10)}-${s.slice(3, 5)}-${s.slice(0, 2)}`)(newCustomer.birth_date),
    };
    this.draftOrderData.newCustomer = null;

    await this.changeOrder({
      url: `orders/${this.order.id}/customer`,
      data: payload,
    });
  }

  get deliverySectionValue(): DeliverySection['value'] {
    return {
      ...this.draftOrderData.delivery,
      city: this.order === null ? null : this.order.city,
    };
  }
  async onDeliverySectionValueChange(...[newVal, whatChanged]: DeliverySectionChangeEvent) {
    if (this.order === null) { return; }

    if (whatChanged === 'city') {
      if (isStubOrder(this.order)) {
        await this.createOrder({ city_id: newVal.city!.id });
      } else {
        await this.changeOrder({ url: `orders/${this.order.id}/city`, data: { city_id: newVal.city!.id } });
      }
    } else {
      // Casting to any because https://github.com/microsoft/TypeScript/issues/31445
      (this.draftOrderData.delivery[whatChanged] as any) = newVal[whatChanged];
      await this.$nextTick(); // So prop values have time to update before validation.
      const deliveryValid = await ((this.$refs.deliverySection as DeliverySection)
        .$refs.validationObserver as InstanceType<typeof ValidationObserver>).validate();
      if (deliveryValid) {
        this.debounceSetDeliveryInfo();
      } else {
        this.debounceSetDeliveryInfo.clear();

        if (whatChanged === 'stock') {
          // But we can still set stock, even thoug the delivery form in its whole is invalid.
          if (isStubOrder(this.order)) {
            await this.createOrder({ stock_id: newVal.stock!.id });
          } else {
            await this.changeOrder({ url: `orders/${this.order.id}/stock`, data: { stock_id: newVal.stock!.id } });
          }
        }
      }
    }
  }
  async setDeliveryInfo() {
    if (this.order === null) return;
    if (isStubOrder(this.order)) throw new Error('Attempted to save delivery info on a stub order');
    if (this.draftOrderData.delivery.stock === null && this.draftOrderData.delivery.method === 'pickup') {
      throw new Error('Stock is required for "pickup" delivery type');
    }

    const deliveryData = this.draftOrderData.delivery;
    const payload: PostRequests.SubmitOrderDelivery = {
      delivery_method: deliveryData.method === 'courier' ? DeliveryMethod.DELIVERY : deliveryData.method,
    };
    if (deliveryData.method !== 'pickup') {
      if (this.order.city === null) throw new Error('City must be selected');

      payload.delivery_address = getDeliveryAddressApiData(deliveryData, this.order.city.name);
      payload.deliver_at = getDeliverAtApiData(deliveryData.deliverAt);
    }
    if (deliveryData.stock !== null) {
      payload.stock_id = deliveryData.stock.id;
    }
    await this.changeOrder({ url: `orders/${this.order.id}/delivery`, data: payload });
  }
  debounceSetDeliveryInfo = debounce(this.setDeliveryInfo, inputDebounceTime);

  async setPaymentStatus(paymentId: string, action: 'confirm' | 'cancel') {
    if (this.order === null) {
      return; // TODO?
    }
    if (isStubOrder(this.order)) {
      throw new Error('Stub orders can\'t have non-draft payments. How did you do this?');
    }
    await this.changeOrder({ url: `orders/${this.order.id}/payments/${paymentId}/${action}` });
  }
  async createPayment($event: CreatePaymentEvent) {
    if (this.order === null) {
      return;
    }
    if (isStubOrder(this.order)) {
      throw new Error('Can\'t create payments while the cart is empty');
    }
    const payload: CreatePaymentTransaction = $event;
    await this.changeOrder({
      url: `orders/${this.order.id}/payments`,
      data: payload,
    });
  }
  async refundPayment(...[eventPayload, resolve, reject]: RefundPaymentEvent) {
    if (this.orderId === null) {
      throw new Error('Order ID is not set');
    }
    const reqPayload: RefundPaymentTransaction = {
      amount: eventPayload.amount,
    };
    const result = await postWithPin(
      `orders/${this.orderId}/payments/${eventPayload.paymentId}/refund`, reqPayload, eventPayload.pin,
    );
    if (result.type !== 'success') {
      if (result.type === 'unhandled') {
        reject(result.err);
        throw result.err;
      }
      if (result.type === 'incorrect-pin') {
        reject('incorrect-pin');
        return;
      }
    }
    resolve();
    await this.loadOrder();
  }
  async cancelPendingRefund(...[eventPayload, resolve, reject]: CancelPendingRefundPaymentEvent) {
    if (this.orderId === null) {
      throw new Error('Order ID is not set');
    }
    // TODO handle the case when the order was loaded with pending refund, but it has later been performed.
    // Maybe it's better to track time when it gets outdated and refresh it?
    const result = await postWithPin(
      `orders/${this.orderId}/payments/${eventPayload.paymentTransactionId}/refund/cancel`, undefined, eventPayload.pin,
    );
    if (result.type !== 'success') {
      if (result.type === 'unhandled') {
        reject(result.err);
        throw result.err;
      }
      if (result.type === 'incorrect-pin') {
        reject('incorrect-pin');
        return;
      }
    }
    resolve();
    await this.loadOrder();
  }

  async onNotesChange(newVal: this['draftOrderData']['notes']) {
    if (isStubOrder(this.order!)) return; // We'll save it when we create the order.
    this.debounceSetNotes();
  }
  async setNotes() {
    if (this.order === null) throw new Error('Attempted to set notes while order is loading');
    if (isStubOrder(this.order)) return; // We'll save it when we create the order.
    const data: Pick<PostRequests.SubmitOrder, 'notes'> = {
      notes: this.draftOrderData.notes,
    };
    await this.changeOrder({ url: `orders/${this.order.id}/notes`, data });
  }
  debounceSetNotes = debounce(this.setNotes, inputDebounceTime);

  async validate(): Promise<boolean> {
    return (this.$refs.validationObserver as InstanceType<typeof ValidationObserver>).validate();
  }

  get headerId(): string {
    return `${this.formId}-header`;
  }
}
